/** Google Analytics 4 — index.html Google Tag + consent; SPA olayları gtag hazır olunca. */

export const GA_MEASUREMENT_ID =
  import.meta.env.VITE_GA_MEASUREMENT_ID?.trim() || "G-Q826CRXP8F";

/** Tag Assistant'daki Google Tag (GT) kimliği — gtag.js bununla yüklenir. */
export const GA_TAG_ID =
  import.meta.env.VITE_GA_TAG_ID?.trim() || "GT-PHXVSZ3";

export function isAnalyticsEnabled(): boolean {
  if (!GA_MEASUREMENT_ID) return false;
  if (import.meta.env.PROD) return true;
  return import.meta.env.VITE_GA_ENABLED === "true";
}

type GtagFn = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: GtagFn;
    google_tag_manager?: Record<string, unknown>;
    __enaGtagReady?: boolean;
  }
}

function callGtag(...args: unknown[]): void {
  if (typeof window.gtag !== "function") return;
  (window.gtag as (...a: unknown[]) => void)(...args);
}

export function gtag(...args: unknown[]): void {
  if (!isAnalyticsEnabled()) return;
  callGtag(...args);
}

function gtagScriptSrc(): string {
  return `https://www.googletagmanager.com/gtag/js?id=${GA_TAG_ID}`;
}

function hasGtagSnippetInDom(): boolean {
  if (typeof document === "undefined") return false;
  return Boolean(
    document.querySelector('script[src*="googletagmanager.com/gtag/js"]') ||
      document.querySelector('script[src="/ga-init.js"]')
  );
}

function isGtagLibraryReady(): boolean {
  return Boolean(window.google_tag_manager?.[GA_TAG_ID] ?? window.google_tag_manager?.[GA_MEASUREMENT_ID]);
}

/** gtag.js yüklendikten sonra özel olaylar — aksi halde isabetler ertelenir. */
export function waitForGtagReady(timeoutMs = 8000): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.__enaGtagReady || isGtagLibraryReady()) {
    window.__enaGtagReady = true;
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const done = () => {
      window.__enaGtagReady = true;
      resolve();
    };

    const onReady = () => done();
    window.addEventListener("ena-gtag-ready", onReady, { once: true });

    const started = Date.now();
    const tick = () => {
      if (isGtagLibraryReady()) {
        window.removeEventListener("ena-gtag-ready", onReady);
        done();
        return;
      }
      if (Date.now() - started >= timeoutMs) {
        window.removeEventListener("ena-gtag-ready", onReady);
        done();
        return;
      }
      globalThis.setTimeout(tick, 80);
    };
    tick();
  });
}

function injectGtagSnippet(): void {
  if (typeof document === "undefined" || hasGtagSnippetInDom()) return;

  const init = document.createElement("script");
  init.async = true;
  init.src = "/ga-init.js";
  document.head.appendChild(init);

  const script = document.createElement("script");
  script.async = true;
  script.src = gtagScriptSrc();
  script.onload = () => window.dispatchEvent(new Event("ena-gtag-ready"));
  document.head.appendChild(script);
}

let analyticsLoadScheduled = false;

/** gtag.js ilk boyamadan sonra yüklenir — PSI kullanılmayan JS uyarısını azaltır. */
export function scheduleAnalyticsLoad(): void {
  if (!isAnalyticsEnabled() || analyticsLoadScheduled) return;
  analyticsLoadScheduled = true;

  const load = () => {
    if (!hasGtagSnippetInDom()) injectGtagSnippet();
  };

  // Mutlak gecikme — requestIdleCallback idle'da hemen ateşlenir ve PSI lab'ında gtag'i erken çeker
  const kick = () => {
    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(load, { timeout: 2000 });
    } else {
      load();
    }
  };

  if (document.readyState === "complete") {
    window.setTimeout(kick, 12000);
  } else {
    window.addEventListener("load", () => window.setTimeout(kick, 12000), { once: true });
  }
}

export function trackPageView(pagePath: string, pageTitle?: string): void {
  if (!isAnalyticsEnabled()) return;

  const title = pageTitle || document.title;
  const location = `${window.location.origin}${pagePath}`;

  gtag("config", GA_MEASUREMENT_ID, {
    page_path: pagePath,
    page_title: title,
    page_location: location,
  });
  gtag("event", "page_view", {
    send_to: GA_MEASUREMENT_ID,
    page_path: pagePath,
    page_title: title,
    page_location: location,
  });
}

export function trackEvent(
  eventName: string,
  params?: Record<string, string | number | boolean | undefined>
): void {
  if (!isAnalyticsEnabled()) return;

  const cleaned: Record<string, string | number | boolean> = {
    send_to: GA_MEASUREMENT_ID,
  };
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) cleaned[key] = value;
    }
  }
  gtag("event", eventName, cleaned);
}

function linkLabel(el: HTMLAnchorElement): string {
  const text = el.textContent?.trim().replace(/\s+/g, " ");
  if (text) return text.slice(0, 80);
  return el.getAttribute("aria-label")?.trim() || el.pathname || "link";
}

function classifyLink(href: string): { event: string; extra?: Record<string, string> } | null {
  try {
    const url = new URL(href, window.location.origin);
    const host = url.hostname.toLowerCase();

    if (url.protocol === "mailto:") {
      return { event: "contact_email_click", extra: { link_url: href } };
    }
    if (url.protocol === "tel:") {
      return { event: "contact_phone_click", extra: { link_url: href } };
    }

    if (host.includes("panel.enabase.com")) {
      if (url.pathname.includes("register")) {
        return { event: "sign_up", extra: { link_url: href, outbound: "true" } };
      }
      return { event: "login", extra: { link_url: href, outbound: "true" } };
    }

    if (host !== window.location.hostname && url.protocol.startsWith("http")) {
      return { event: "click", extra: { link_url: href, outbound: "true" } };
    }
  } catch {
    return null;
  }
  return null;
}

let autoTrackingBound = false;
const scrollMarks = new Set<number>();

function currentPagePath(): string {
  return `${window.location.pathname}${window.location.search}`;
}

export function setupAnalyticsAutoTracking(): void {
  if (!isAnalyticsEnabled() || autoTrackingBound) return;
  autoTrackingBound = true;

  void waitForGtagReady().then(() => {
    document.addEventListener(
      "click",
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const gaEl = target.closest("[data-ga-event]");
        if (gaEl instanceof HTMLElement && gaEl.dataset.gaEvent) {
          const name = gaEl.dataset.gaEvent;
          const params: Record<string, string> = {
            event_category: gaEl.dataset.gaCategory || "engagement",
            event_label:
              gaEl.dataset.gaLabel ||
              (gaEl instanceof HTMLAnchorElement
                ? linkLabel(gaEl)
                : gaEl.innerText?.trim().slice(0, 80) || name),
          };
          if (gaEl instanceof HTMLAnchorElement && gaEl.href) {
            params.link_url = gaEl.href;
          }
          trackEvent(name, params);
          return;
        }

        const anchor = target.closest("a[href]");
        if (!(anchor instanceof HTMLAnchorElement)) return;

        const classified = classifyLink(anchor.href);
        if (!classified) return;

        trackEvent(classified.event, {
          event_category: "engagement",
          event_label: linkLabel(anchor),
          ...classified.extra,
        });
      },
      { capture: true, passive: true }
    );

    const onScroll = () => {
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      if (max <= 0) return;
      const ratio = window.scrollY / max;
      for (const mark of [25, 50, 75, 90]) {
        if (ratio >= mark / 100 && !scrollMarks.has(mark)) {
          scrollMarks.add(mark);
          trackEvent("scroll_depth", {
            event_category: "engagement",
            percent_scrolled: mark,
            page_path: currentPagePath(),
          });
        }
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
  });
}

export function resetScrollTracking(): void {
  scrollMarks.clear();
}
